// @ts-check
import { OrganisationSearch } from 'components/Search/Results/helpers';
import { noValue } from 'utils/search';
import { mapAiFiltersToOrgSlice } from './aiFilterAllowlist';

/**
 * Marks a link as wanting the current search's filters. A bare token rather than
 * a payload: the filters already live in the user's own browser, so the link only
 * has to say "use them". Nothing to encode means nothing to corrupt, it needs no
 * validation on arrival, and pasting the link elsewhere cannot carry anyone's
 * search to anyone else.
 */
export const CARRY_FILTERS_PARAM = 'useMainSearchFilters';

/**
 * Keys that drive the search machinery rather than describing what to match.
 * Removed before mapping so they are never reported to the user as filters that
 * could not be applied — the user never set them.
 * @type {ReadonlyArray<string>}
 */
const searchMachineryKeys = [
  'refreshSearch',
  'followUpQueries',
  'aiFilterSessionId',
];

/**
 * Location keys that describe the ORGANISATION on the main-search Organisations
 * tab and the PERSON everywhere else.
 *
 * `buildQuery` remaps these to their super-organisation equivalents, but only
 * while the view is an organisation search. The profile searches with no view, so
 * carrying them verbatim from the Organisations tab silently re-reads "companies
 * in Cambridge" as "people in Cambridge" — hiding everyone at the company who
 * sits elsewhere. Measured at 18% of one large organisation.
 *
 * `organisationGeography` belongs here despite its name. `orgGeoFormat` picks its
 * index field from the key it is handed, and only the Organisations tab's remap to
 * `superOrganisationGeography` reaches the organisation's own location; from any
 * other tab it falls through to `Country`, the person's. So it is person-scoped
 * on the Contacts and Activities tabs, and carries there — renamed to the
 * profile's `country` by the shared allowlist's translation.
 * @type {ReadonlyArray<string>}
 */
const organisationTabLocationKeys = ['city', 'state', 'organisationGeography'];

/**
 * The named-author checkboxes. Main search holds them nested under this key and
 * so does the profile, but the shared allowlist speaks the AI's dialect, where
 * they arrive as flat siblings and get folded into the group. Handing the object
 * over whole therefore reads as an unrecognised key and loses the filter, so the
 * members go over flat and the existing fold rebuilds them.
 */
const namedAuthorGroupKey = 'namedAuthor';

/**
 * Whether a value is a plain object, and so safe to spread as a filter group.
 * @param {any} value
 */
const isPlainGroup = value =>
  value !== null && typeof value === 'object' && value.constructor === Object;

/**
 * The date dropdown's unrestricted setting. Every search starts here.
 */
const unrestrictedDate = 'all';

/**
 * Custom chosen in the dropdown, before a range has been picked. The search
 * waits for the range, so nothing is filtered yet.
 */
const pendingCustomDate = 'custom';

/**
 * Whether the date dropdown is holding results back. An absent value is the
 * dropdown having never been touched, which restricts nothing.
 * @param {any} value
 */
export const isRestrictedDate = value =>
  value != null && value !== unrestrictedDate && value !== pendingCustomDate;

/**
 * The category keys selected in a "Search across" group, sorted so two groups can
 * be compared regardless of insertion order.
 * @param {Record<string, boolean> | null | undefined} selection
 * @returns {string[]}
 */
const selectedCategories = selection =>
  Object.entries(selection ?? {})
    .filter(([, isOn]) => isOn === true)
    .map(([key]) => key)
    .sort();

/**
 * Whether a "Search across" selection differs from the one every search starts
 * with. The categories are always populated once a search area has rendered, so
 * only a difference from the default says the user chose to narrow.
 *
 * Two states mean "no answer" rather than "no narrowing": no default to compare
 * against, and nothing selected at all — a search across nothing is a slice that
 * has not been seeded yet, not a choice the user made.
 *
 * @param {Record<string, boolean> | null | undefined} selection
 * @param {Record<string, boolean> | null | undefined} defaultCategories from useDefaultCategories
 */
export const hasNarrowedCategories = (selection, defaultCategories) => {
  if (!defaultCategories) return false;

  const selected = selectedCategories(selection);
  if (selected.length === 0) return false;

  const expected = selectedCategories(defaultCategories);
  return (
    selected.length !== expected.length ||
    selected.some((category, i) => category !== expected[i])
  );
};

/**
 * Whether one key of a carried bag represents something the user narrowed, as
 * opposed to the scope every search starts with.
 *
 * Two keys need this. `date` and the "Search across" categories are populated on
 * every single search, so counting them blindly would put the pointer on every
 * link and a denominator on every profile. Ignoring them is worse though: narrowing to
 * Clinical Trials alone is exactly the kind of intent worth carrying, and the
 * profile seeds its categories from the same default, so an untouched selection
 * changes nothing there either way.
 *
 * @param {string} key org-slice key
 * @param {any} value
 * @param {Record<string, boolean> | null | undefined} defaultCategories from useDefaultCategories
 * @returns {boolean}
 */
function isNarrowing(key, value, defaultCategories) {
  if (key === 'date') {
    return isRestrictedDate(value);
  }

  if (key === 'workItemCategories') {
    return hasNarrowedCategories(value, defaultCategories);
  }

  return true;
}

/**
 * Copies a filter value so the profile's slice never shares mutable structure
 * with the search slice. Handing the reference over looks harmless but is not:
 * removing a token splices the array in place before dispatching a copy of it, so
 * a shared array means clearing a filter here also clears it on the main search.
 *
 * Arrays and plain objects are rebuilt; anything else — including dayjs values,
 * which must keep their prototype and are never mutated in place — is passed
 * through, which is why this is not a JSON round-trip.
 *
 * @param {any} value
 * @returns {any}
 */
function cloneFilterValue(value) {
  if (Array.isArray(value)) {
    return value.map(cloneFilterValue);
  }

  if (
    value !== null &&
    typeof value === 'object' &&
    value.constructor === Object
  ) {
    return Object.fromEntries(
      Object.entries(value).map(([key, member]) => [
        key,
        cloneFilterValue(member),
      ])
    );
  }

  return value;
}

/**
 * @typedef {Object} CarriedFilters
 * @property {Record<string, any>} filters org-slice-shaped filters to apply
 * @property {string[]} dropped keys the profile cannot honour, so the drop rules can
 *   be asserted as rules rather than only as absences
 */

/**
 * Map a main-search filter bag onto the organisation profile's shared
 * People/Activity slice, given the view the search was run under.
 *
 * Shares its allow-set with the SciSearch AI apply path so the two cannot drift;
 * the source view is the one thing that path has no need of, because the AI emits
 * profile-shaped filters directly.
 *
 * @param {Record<string, any> | null | undefined} searchFilters state.search.filters
 * @param {string | null | undefined} sourceView state.search.view at click time
 * @returns {CarriedFilters}
 */
export function mapSearchFiltersToOrgSlice(searchFilters, sourceView) {
  if (!searchFilters) {
    return { filters: {}, dropped: [] };
  }

  const organisationScoped = OrganisationSearch(sourceView)
    ? organisationTabLocationKeys
    : [];

  const carryable = {};
  const scopeDropped = [];

  for (const [key, value] of Object.entries(searchFilters)) {
    if (searchMachineryKeys.includes(key)) continue;
    // An unset filter is not a filter the profile failed to apply.
    if (noValue(value)) continue;

    if (organisationScoped.includes(key)) {
      scopeDropped.push(key);
      continue;
    }

    if (key === namedAuthorGroupKey && isPlainGroup(value)) {
      Object.assign(carryable, cloneFilterValue(value));
      continue;
    }

    carryable[key] = cloneFilterValue(value);
  }

  const { filters, dropped } = mapAiFiltersToOrgSlice(carryable);

  return { filters, dropped: [...scopeDropped, ...dropped] };
}

/**
 * True when a search has narrowed something the profile can honour, so a link to
 * an organisation is worth pointing at those filters. False for an untouched
 * search, which must keep producing exactly the link it produces today.
 *
 * @param {Record<string, any> | null | undefined} searchFilters
 * @param {string | null | undefined} sourceView
 * @param {Record<string, boolean> | null | undefined} [defaultCategories] from useDefaultCategories
 * @returns {boolean}
 */
export function hasCarryableFilters(
  searchFilters,
  sourceView,
  defaultCategories
) {
  const { filters } = mapSearchFiltersToOrgSlice(searchFilters, sourceView);
  return Object.entries(filters).some(([key, value]) =>
    isNarrowing(key, value, defaultCategories)
  );
}
